<route lang="yaml">
  meta:
    layout: default
    header: true
  </route>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useValoracionesStore } from '@/stores/valoraciones';
import AudioStoriesCarousel from '@/components/AudioStoriesCarousel.vue';
import AudioStoriesCarouselMobile from '@/components/AudioStoriesCarouselMobile.vue';
import { getCuentosWithVersions, getCuentosMejorValorados, transformCuentosData } from '@/services/cuentos';
import { useHead } from '@vueuse/head';
import * as XLSX from 'xlsx';
import PullToRefresh from '@/components/PullToRefresh.vue';
import { usePullToRefresh } from '@/composables/usePullToRefresh';
import TallerVirtualBanner from '@/components/TallerVirtualBanner.vue';

// Restaurar título y meta por defecto
useHead({
  title: 'Sonoro Orsai',
  meta: [
    { property: 'og:title', content: 'Sonoro Orsai' },
    { property: 'og:description', content: 'Más de 3.000 alumnos del taller de Hernán Casciari escribieron historias breves, y acá podés escucharlas todas.' },
    { property: 'og:image', content: '/assets/images/Navbar/logo_orsai_png.png' },
    { property: 'og:url', content: typeof window !== 'undefined' ? window.location.origin : '' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Sonoro Orsai' },
    { name: 'twitter:description', content: 'Más de 3.000 alumnos del taller de Hernán Casciari escribieron historias breves, y acá podés escucharlas todas.' },
    { name: 'twitter:image', content: '/assets/images/Navbar/logo_orsai_png.png' },
  ],
});

// Usar el store directamente
const authStore = useAuthStore();
const valoracionesStore = useValoracionesStore();

// Estados para los datos de los carruseles
const cuentosParaDisfrutar = ref([]);
const cuentosMasValorados = ref([]);
const cuentosDramas = ref([]);

// Estados para los datos dinámicos del Google Sheets
const sheetData = ref(null);
const loadingSheetData = ref(true);

// Estados de carga - inicializar como true para mostrar skeleton inmediatamente
const loadingParaDisfrutar = ref(true);
const loadingMasValorados = ref(true);
const loadingDramas = ref(true);

// Estados de error
const errorParaDisfrutar = ref(false);
const errorMasValorados = ref(false);
const errorDramas = ref(false);

// Computed para procesar las secciones ordenadas
const seccionesOrdenadas = computed(() => {
  if (!sheetData.value?.sections) return [];
  
  return sheetData.value.sections.map(section => {
    switch (section.tipo) {
      case 'cuentos_recientes':
        return {
          ...section,
          loading: loadingParaDisfrutar.value,
          error: errorParaDisfrutar.value,
          stories: cuentosParaDisfrutar.value.slice(0, 50),
          onRetry: cargarCuentosParaDisfrutar
        };
      case 'mas_valorados':
        return {
          ...section,
          loading: loadingMasValorados.value,
          error: errorMasValorados.value,
          stories: cuentosMasValorados.value.slice(0, 9),
          onRetry: cargarCuentosMasValorados
        };
      case 'por_slug':
        return {
          ...section,
          loading: loadingDramas.value,
          error: errorDramas.value,
          stories: cuentosDramas.value.slice(0, 9),
          onRetry: cargarCuentosDramas
        };
      case 'banner':
        return {
          ...section,
          type: 'banner'
        };
      default:
        return null;
    }
  }).filter(section => section !== null);
});

// --- SWITCH AUTOMÁTICO MOBILE/DESKTOP ---
const isMobile = ref(window.innerWidth < 640);
function handleResize() {
  isMobile.value = window.innerWidth < 640;
}
onMounted(() => {
  window.addEventListener('resize', handleResize);
});

// Limpieza del event listener
onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
// ----------------------------------------

console.log('🏠 Página index - Usuario:', authStore.usuario);
console.log('🏠 Página index - isAuthenticated:', authStore.isAuthenticated);

// Función para cargar cuentos
const cargarCuentos = async (page, version = 'V4', filters = {}, pageSize = 24) => {
  try {
    const response = await getCuentosWithVersions(page, version, filters, pageSize);
    return transformCuentosData(response.data);
  } catch (error) {
    console.error(`❌ Error al cargar cuentos de la página ${page}:`, error);
    throw error;
  }
};

function cargarValoracionesDeCuentos(lista) {
  lista.forEach(cuento => {
    if (cuento.documentId && !valoracionesStore.estadisticas[cuento.documentId]) {
      valoracionesStore.cargarEstadisticas(cuento.documentId);
    }
  });
}

// Cargar cuentos para "Para disfrutar escuchando" (página 1)
const cargarCuentosParaDisfrutar = async () => {
  loadingParaDisfrutar.value = true;
  errorParaDisfrutar.value = false;
  
  try {
    const cuentos = await cargarCuentos(1, 'V4', {}, 50);
    cuentosParaDisfrutar.value = cuentos;
    cargarValoracionesDeCuentos(cuentos);
    console.log('✅ Cuentos para disfrutar cargados:', cuentos.length);
  } catch (error) {
    errorParaDisfrutar.value = true;
    console.error('❌ Error al cargar cuentos para disfrutar:', error);
  } finally {
    loadingParaDisfrutar.value = false;
  }
};

// Cargar cuentos para "Los más valorados" desde endpoint específico
const cargarCuentosMasValorados = async () => {
  loadingMasValorados.value = true;
  errorMasValorados.value = false;
  
  try {
    const response = await getCuentosMejorValorados();
    const cuentos = transformCuentosData(response.data);
    cuentosMasValorados.value = cuentos;
    cargarValoracionesDeCuentos(cuentos);
    console.log('✅ Cuentos más valorados cargados:', cuentos.length);
  } catch (error) {
    errorMasValorados.value = true;
    console.error('❌ Error al cargar cuentos más valorados:', error);
  } finally {
    loadingMasValorados.value = false;
  }
};

// Cargar cuentos para "Dramas" usando el SLUG del Google Sheets
const cargarCuentosDramas = async () => {
  loadingDramas.value = true;
  errorDramas.value = false;
  
  try {
    // Buscar la sección de tipo 'por_slug' en las secciones ordenadas
    const seccionPorSlug = sheetData.value?.sections?.find(section => section.tipo === 'por_slug');
    const slug = seccionPorSlug?.slug;
    
    if (!slug) {
      console.warn('⚠️ No se encontró slug para la sección por_slug');
      cuentosDramas.value = [];
      return;
    }
    
    const cuentos = await cargarCuentos(1, 'V4', { tags: slug });
    cuentosDramas.value = cuentos;
    cargarValoracionesDeCuentos(cuentos);
    console.log(`✅ Cuentos cargados para slug "${slug}":`, cuentos.length);
    console.log(`📝 Título de la sección: "${seccionPorSlug?.titulo}"`);
  } catch (error) {
    errorDramas.value = true;
    console.error('❌ Error al cargar cuentos dramas:', error);
  } finally {
    loadingDramas.value = false;
  }
};

// Función para obtener datos del Google Sheets CSV
const obtenerDatosGoogleSheets = async () => {
  loadingSheetData.value = true;
  try {
    const response = await fetch('https://docs.google.com/spreadsheets/d/e/2PACX-1vQWBlQ7HDJqR37f4n2_iPk-e887jGyZoXgfVXW3GTmX40Of1PD4tX8E2Lhazio6ToBneoBUz28L0iF_/pub?gid=1724104960&single=true&output=csv');
    const csvText = await response.text();
    
    // Parsear CSV usando SheetJS con opciones específicas para CSV
    const workbook = XLSX.read(csvText, { 
      type: 'string',
      raw: true,
      cellDates: true,
      cellNF: false,
      cellText: false
    });
    
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    
    // Convertir a JSON con headers como primera fila
    const rawData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1,
      defval: '',
      blankrows: false
    });
    
    // Procesar los datos para obtener una estructura más útil
    const headers = rawData[0];
    const dataRows = rawData.slice(1); // Todas las filas excepto la primera (headers)
    
    console.log('📋 Headers encontrados:', headers);
    console.log('📋 Primera fila de datos:', dataRows[0]);
    
    // Función helper para obtener valor por nombre de columna
    const getValueByColumnName = (row, columnName) => {
      // Normalizar el nombre de la columna para búsqueda más flexible
      const normalizedColumnName = columnName.toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''); // Remover acentos
      
      const columnIndex = headers.findIndex(header => {
        if (!header) return false;
        const normalizedHeader = header.toString().toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, ''); // Remover acentos
        return normalizedHeader.includes(normalizedColumnName);
      });
      if (columnIndex === -1) {
        console.log(`⚠️ Columna "${columnName}" no encontrada. Headers disponibles:`, headers);
        return '';
      }
      
      let value = row[columnIndex];
      if (value === undefined || value === null) return '';
      
      // Convertir a string y limpiar
      value = String(value);
      
      // Remover comillas dobles al inicio y final si existen
      value = value.replace(/^"|"$/g, '');
      
      // Reemplazar saltos de línea múltiples con espacios simples
      value = value.replace(/\n+/g, ' ');
      
      // Limpiar espacios extra
      value = value.trim();
      
      console.log(`🔍 Valor para "${columnName}": "${value}" (columna ${columnIndex})`);
      
      return value;
    };
    
    // Procesar cada fila como una sección
    const sections = dataRows
      .map((row, index) => {
        const orden = parseInt(getValueByColumnName(row, 'orden')) || index + 1;
        const tipo = getValueByColumnName(row, 'tipo');
        const activo = getValueByColumnName(row, 'activo').toLowerCase() === 'true';
        const titulo = getValueByColumnName(row, 'titulo');
        const copete = getValueByColumnName(row, 'copete');
        const slug = getValueByColumnName(row, 'slug');
        
        console.log(`📝 Procesando fila ${index + 1}:`, {
          orden,
          tipo,
          titulo,
          copete,
          slug,
          activo,
          rawRow: row
        });
        
        if (!tipo || !activo) return null;
        
        return {
          orden,
          tipo,
          titulo,
          copete,
          slug
        };
      })
      .filter(section => section !== null)
      .sort((a, b) => a.orden - b.orden); // Ordenar por el campo orden
    
    // Crear objeto estructurado
    const structuredData = {
      headers: headers,
      sections: sections
    };
    
    console.log('📊 Datos CSV original:', csvText);
    console.log('📊 Datos parseados con SheetJS:', rawData);
    console.log('📊 Secciones ordenadas:', structuredData);
    
    // Guardar los datos en el estado reactivo
    sheetData.value = structuredData;
    
    return structuredData;
  } catch (error) {
    console.error('❌ Error al obtener datos del Google Sheets:', error);
    throw error;
  } finally {
    loadingSheetData.value = false;
  }
};

// Usar el composable de pull-to-refresh
const { createCuentosRefreshHandler } = usePullToRefresh();

// Función para manejar el pull-to-refresh
const handleRefresh = createCuentosRefreshHandler([
  obtenerDatosGoogleSheets,
  cargarCuentosParaDisfrutar,
  cargarCuentosMasValorados,
  cargarCuentosDramas
], {
  minInterval: 3000,
  onStart: () => console.log('🔄 Recargando home...'),
  onSuccess: () => console.log('✅ Home recargado'),
  onError: (errors) => console.error('❌ Error al recargar home:', errors)
});

// Cargar todos los datos al montar el componente
onMounted(async () => {
  console.log('🚀 Iniciando carga de cuentos...');
  
  // Obtener datos del Google Sheets primero
  await obtenerDatosGoogleSheets();
  
  // Cargar todos los carruseles en paralelo después de tener los datos del Google Sheets
  await Promise.all([
    cargarCuentosParaDisfrutar(),
    cargarCuentosMasValorados(),
    cargarCuentosDramas() // Ahora puede usar el slug del Google Sheets
  ]);
  
  console.log('✅ Todos los cuentos cargados');
});
</script>

<template>
  <PullToRefresh :on-refresh="handleRefresh" :threshold="80" :resistance="2.5">
    <div class="min-h-screen bg-gray-50 py-4">

    <!-- Main Content -->
    <div class="max-w-6xl mx-auto px-4 md:pt-6 gap-10 flex flex-col">
      <!-- Renderizado dinámico de secciones -->
      <template v-for="seccion in seccionesOrdenadas" :key="seccion.orden">
        <!-- Sección de cuentos (cualquier tipo) -->
        <div v-if="seccion.type !== 'banner'" class="flex flex-col">
          <h2 v-if="!seccion.loading" class="text-2xl font-bold">
            {{ seccion.titulo }}
          </h2>
          <p v-if="!seccion.loading" class="text-gray-600 text-sm mb-2 md:mb-0 leading-tight md:leading-normal">
            {{ seccion.copete }}
          </p>
          <component
            :is="isMobile ? AudioStoriesCarouselMobile : AudioStoriesCarousel"
            :stories="seccion.stories"
            :loading="seccion.loading"
            :error="seccion.error"
            :cardsPerSlide="isMobile ? 3 : undefined"
            :title="seccion.titulo"
            :subtitle="seccion.copete"
            @retry="seccion.onRetry"
          />
        </div>
        
        <!-- Banner -->
        <TallerVirtualBanner v-else-if="seccion.type === 'banner'" />
      </template>
    </div>
    </div>
  </PullToRefresh>
</template>

<style scoped>
/* Estilos básicos para el home limpio */
.splide__slide {
  /* Fuerza a todos los slides a tener el mismo alto */
  display: flex;
  flex-direction: column;
  height: 260px; /* Ajusta este valor según el diseño */
}
.splide__list {
  align-items: flex-start !important; /* Alinea todos arriba */
}
</style>